import styled, { StyledComponentPropsWithRef } from 'styled-components'

import { hideVisually } from 'polished'

import { preventDefaultLinkClickBehavior, type AppRoute } from 'router'
import { theme } from 'styles/theme'
import { ChevronIcon, DotsIcon, InfoIcon, MagnifyingGlassIcon, PlusIcon } from '../Icon/Icon'

import type { ReactNode } from 'react'

const ButtonText = styled.span<WithButtonStyle>`
  line-height: ${theme.lineHeights.normal};
  font-weight: ${theme.fontWeights.bold};
  font-family: ${theme.fonts.buttonsAndTitlesFont};
  text-transform: uppercase;

  padding-inline: 1em;

  // Yeah... since we always capitalize the button text, it looks like it's ever so slightly off-center vertically. 
  // So we adjust the vertical padding to make it look more centered. It's ugly, but here it is.
  padding-top: calc(${({buttonStyle}) => buttonStyle === 'thin' ? '0.4em' : '0.6em'} + 1px);
  padding-bottom: calc(${({buttonStyle}) => buttonStyle === 'thin' ? '0.4em' : '0.6em'} - 1px);

  white-space: nowrap;
  /* Using with startComponent */
  * + &[class] {
    padding-inline-start: 0;
  }
  /* Using with endComponent */
  &[class] + * {
    margin-inline-start: 0;
  }
`

interface WithButtonStyle {
  buttonStyle: 'regular' | 'thin'
}
const StyledButton = styled.button`
  min-inline-size: 100px;
  padding: 0 0.7em;
  gap: 0.5em;
  /* font-size is here and not in ButtonText so we can use it in the cascade */
  font-size: ${theme.fontSizes.medium};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radii.large};
  transition: all ${theme.transitions.normal} ease-out;
  :disabled {
    cursor: auto;
  }
`

// AppRoute is a union, which forces us to use the `type` syntax here instead of `interface`.
// https://stackoverflow.com/a/56085307/915875
export type ButtonProps = MaybeWithIsDisabled &
  MaybeWithTitle &
  WithText &
  Partial<AppRoute> &
  StyledComponentPropsWithRef<typeof StyledButton> & 
  Partial<WithButtonStyle> & {
    tagName?: 'button' | 'span' | 'a'
    startComponent?: ReactNode
    endComponent?: ReactNode
  }

const Button = styled(
  ({
    text,
    title = text,
    children,
    type = 'button',
    isDisabled = false,
    startComponent,
    endComponent,
    href,
    tagName = Boolean(href) ? 'a' : 'button',
    push,
    buttonStyle = 'regular',
    onClick = push,
    ...props
  }: ButtonProps) => {
    const typeOrHrefAttribute = tagName === 'button' ? ({ type } as const) : ({ href } as const)
    return (
      <StyledButton
        {...{
          ...typeOrHrefAttribute,
          disabled: isDisabled,
          as: tagName,
          title,
          buttonStyle,
          onClick(e) {
            // Non <button>s don't have a [disabled] attribute, so we need to check for it.
            if (!isDisabled && onClick) {
              onClick(e)
            }
            if (tagName === 'a' && preventDefaultLinkClickBehavior(e) && push) {
              push()
            }
          },
          ...props,
        }}
      >
        {startComponent}
        {children ?? <ButtonText buttonStyle={buttonStyle}>{text}</ButtonText>}
        {endComponent}
      </StyledButton>
    )
  },
)``

export const PrimaryButton = styled(Button)`
  color: ${theme.colors.MainBlack};
  background-color: ${theme.colors.MainYellow};

  :hover:not(:disabled) {
    border-color: ${theme.colors.MainYellowOnHover};
    background-color: ${theme.colors.MainYellowOnHover};
  }
  :focus:active {
    border-color: ${theme.colors.SemiDarkYellow};
    background-color: ${theme.colors.SemiDarkYellow};
  }

  &[disabled] {
    opacity: 0.3;
    :is(a[href]) {
      /* TODO: figure out how to set cursor and title without modifying markup for a disabled link */
      pointer-events: none;
    }
  }
`

export const PrimaryPlusButton = (props: ButtonProps) => (
  <PrimaryButton {...{ startComponent: <PlusIcon />, ...props }} />
)

export const PrimaryMenuButton = (props: ButtonProps) => (
  <PrimaryButton {...{ endComponent: <ChevronIcon />, ...props }} />
)

const PrimaryIconButton = styled(PrimaryButton)`
  min-inline-size: 0;
  ${ButtonText} {
    ${hideVisually()};
  }
  > svg[class] {
    margin: 0.7em;
  }
`

// Wrapped in styled for interpolation
export const PrimarySearchIconButton = styled((props: ButtonProps) => (
  <PrimaryIconButton {...{ endComponent: <MagnifyingGlassIcon />, ...props }} />
))``

export const SecondaryButton = styled(Button)`
  color: ${theme.colors.MainBlack};
  border-color: currentColor;
  border-style: solid;
  border-width: 2px;
  :hover:not(:disabled),
  :focus-visible {
    color: ${theme.colors.White};
    > svg * {
      fill: ${theme.colors.White};
    }
    background-color: ${theme.colors.SecondaryDarkGrey};
    border-color: ${theme.colors.SecondaryDarkGrey};
  }
  &[disabled] {
    color: ${theme.colors.Gray7};

    > svg * {
      fill: ${theme.colors.Gray7};
    }
  }
`

export const SecondaryMenuButton = (props: ButtonProps) => (
  <SecondaryButton {...{ endComponent: <ChevronIcon />, ...props }} />
)

export type GhostButtonProps = ButtonProps & MaybeWithIsDanger

export const GhostButton = styled(Button)`
  color: ${({ isDanger = false }: GhostButtonProps) => (isDanger ? theme.colors.danger : 'unset')};
  border-color: currentColor;
  min-inline-size: 0;
  padding: 0;
  gap: 0;
  &[disabled] {
    color: ${theme.colors.Gray7};
  }
  > svg {
    z-index: 1; // make it appear above TdAnchorForLink, if it exists
    inline-size: var(--size, 1.5em);
    block-size: var(--block-size, 1.5em);
    flex-shrink: 0;
    * {
      fill: ${({ isDisabled = false }: GhostButtonProps) => (isDisabled ? theme.colors.Gray7 : theme.colors.MainBlack)};
    }
    :hover:not(:disabled) * {
      color: ${({ isDisabled = false }: GhostButtonProps) => (isDisabled ? theme.colors.Gray7 : theme.colors.MainYellow)}; // the line border color
      fill: ${({ isDisabled = false }: GhostButtonProps) => (isDisabled ? theme.colors.Gray7 : theme.colors.MainYellow)};
    }
  }

  position: relative;
  ${ButtonText} {
    ${hideVisually()};
  }
`

// Wrapped in `styled` (without styling) so interpolations of it in other styled components will work.
export const DotsGhostButton = styled(({ isDisabled = false, ...props }: ButtonProps) => {
  return (
    <GhostButton
      isDisabled={isDisabled}
      startComponent={<DotsIcon />}
      {...props}
    />
  )
})``

export const InfoGhostButton = styled(({isDisabled = false, ...props }: ButtonProps) => {
  return (
    <GhostButton
      isDisabled={isDisabled}
      startComponent={<InfoIcon />}
      {...props}
    />
  )
})``

interface PageNumberButtonProps {
  isCurrentPage: boolean
}

export const PageNumberButton = styled(Button)<PageNumberButtonProps>`
  min-inline-size: 0;
  border: 1px solid ${theme.colors.Gray8};
  border-inline-end: none;
  border-radius: 0;
  &:first-of-type {
    border-radius: 3px 0 0 3px;
  }
  &:last-of-type {
    border-radius: 0 3px 3px 0;
    border-inline-end: 1px solid ${theme.colors.Gray8};
  }
  &:only-of-type {
    border-radius: 3px;
  }
  background-color: ${({ isCurrentPage }) => (isCurrentPage ? theme.colors.SecondaryYellow : 'inherit')};
`

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1em;
`

export type ButtonType = typeof Button
